// Interactive Music Studio catalog.
//
// For now the studio is powered entirely by the bundled bell sounds that ship
// in /public/bells (see constants/BellSounds). Each file is wrapped in a
// richer "track" shape (title, artist, category, gradient cover) so the UI can
// present them as an album-style library. Swap `buildCatalog` for a real API
// call later without touching the page component.
import {BELL_SOUND_FILES} from "constants/BellSounds";

export const CATEGORIES = [
    {key: "all", label: "All"},
    {key: "music", label: "Music"},
    {key: "podcast", label: "Podcasts"},
    {key: "bells", label: "Bells"},
];

// Per-file presentation metadata. Anything not listed falls back to a generic
// "Bells" entry so newly-added files still render.
const TRACK_META = {
    "bell.mp3": {title: "Classic Bell", artist: "School Tones", category: "bells", album: "Everyday Chimes"},
    "electric-bell.mp3": {title: "Electric Bell", artist: "School Tones", category: "bells", album: "Everyday Chimes"},
    "japanese-bell.mp3": {title: "Zen Temple Bell", artist: "Ambient Rings", category: "bells", album: "Calm Corners"},
    "emergency.mp3": {title: "Emergency Alarm", artist: "Safety First", category: "bells", album: "Alerts"},
    "fire.mp3": {title: "Fire Drill", artist: "Safety First", category: "bells", album: "Alerts"},
    "national-anthem.mp3": {title: "National Anthem", artist: "Assembly", category: "music", album: "Morning Assembly"},
    "public-song.mp3": {title: "Public Song", artist: "Assembly", category: "music", album: "Morning Assembly"},
    "lunch-greeting.mp3": {title: "Lunch Greeting", artist: "Cafeteria Radio", category: "podcast", album: "Daily Announcements"},
};

// A set of pleasant gradient "covers" so each tile looks like an album.
const COVERS = [
    "linear(135deg, #667eea 0%, #764ba2 100%)",
    "linear(135deg, #f093fb 0%, #f5576c 100%)",
    "linear(135deg, #4facfe 0%, #00f2fe 100%)",
    "linear(135deg, #43e97b 0%, #38f9d7 100%)",
    "linear(135deg, #fa709a 0%, #fee140 100%)",
    "linear(135deg, #30cfd0 0%, #330867 100%)",
    "linear(135deg, #ff9a9e 0%, #fecfef 100%)",
    "linear(135deg, #a18cd1 0%, #fbc2eb 100%)",
    "linear(135deg, #f6d365 0%, #fda085 100%)",
    "linear(135deg, #5ee7df 0%, #b490ca 100%)",
];

const prettify = (fileName) =>
    fileName
        .replace(/\.[^.]+$/, "")
        .replace(/[-_]+/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());

// Cheap deterministic "duration" so the player UI has something to show before
// the audio metadata loads. Real duration replaces it on `loadedmetadata`.
const seedDuration = (name) => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) & 0xffff;
    return 20 + (hash % 160); // 0:20 – 3:00
};

export const coverForIndex = (index) => COVERS[Math.abs(index) % COVERS.length];

// The API doesn't carry a category, so infer one from the name/album for the
// filter tabs. Falls back to "music". Tweak freely once the backend adds a real
// category field.
export const inferCategory = (name = "", album = "") => {
    const text = `${name} ${album}`.toLowerCase();
    if (/(bell|chime|alarm|siren|ring|gong)/.test(text)) return "bells";
    if (/(podcast|episode|talk|announce|greeting|speech)/.test(text)) return "podcast";
    return "music";
};

// API record { id, name, album, artist, s3Url, uploadedBy } → studio track.
export const mapApiTrack = (record = {}, index = 0) => ({
    id: record.id,
    src: record.s3Url || "",
    title: record.name || "Untitled",
    artist: record.artist || "Unknown Artist",
    album: record.album || "Singles",
    uploadedBy: record.uploadedBy || null,
    category: inferCategory(record.name, record.album),
    cover: coverForIndex(typeof record.id === "number" ? record.id : index),
    duration: 0,
});

export const buildCatalog = () =>
    BELL_SOUND_FILES.map((file, index) => {
        const meta = TRACK_META[file] || {
            title: prettify(file),
            artist: "School Tones",
            category: "bells",
            album: "Bell Library",
        };
        return {
            id: file,
            src: `/bells/${file}`,
            title: meta.title,
            artist: meta.artist,
            album: meta.album,
            category: meta.category,
            cover: COVERS[index % COVERS.length],
            duration: seedDuration(file),
        };
    });

export const formatTime = (seconds) => {
    if (!seconds || Number.isNaN(seconds)) return "0:00";
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${String(s).padStart(2, "0")}`;
};
